/**
 * Error Tracking Service
 *
 * Abstraction layer for error tracking (Sentry).
 * Allows error capture with context, user identification, breadcrumbs.
 *
 * @example
 * ```typescript
 * // Initialize at app startup
 * initErrorTracking({
 *   dsn: import.meta.env.VITE_SENTRY_DSN,
 *   environment: import.meta.env.MODE,
 * });
 *
 * // Capture errors
 * errorTracking.captureError(error, { userId: 123 });
 *
 * // Add breadcrumb
 * errorTracking.addBreadcrumb({ message: "User clicked submit" });
 * ```
 */

import * as Sentry from "@sentry/react";

// =============================================================================
// TYPES
// =============================================================================

export interface ErrorTrackingConfig {
  /** Sentry DSN */
  dsn?: string;
  /** Environment (development, staging, production) */
  environment: string;
  /** App version */
  release?: string;
  /** Enable in development */
  enableInDev?: boolean;
  /** Sample rate for errors (0-1) */
  sampleRate?: number;
  /** Sample rate for performance (0-1) */
  tracesSampleRate?: number;
}

export interface ErrorContext {
  userId?: string;
  accountId?: string;
  telegramId?: string;
  extra?: Record<string, unknown>;
  tags?: Record<string, string>;
  level?: "fatal" | "error" | "warning" | "info" | "debug";
}

export interface BreadcrumbData {
  category?: string;
  message: string;
  data?: Record<string, unknown>;
  level?: "fatal" | "error" | "warning" | "info" | "debug";
}

export interface UserContext {
  id: string;
  telegramId?: string;
  accountId?: string;
  [key: string]: unknown;
}

// =============================================================================
// STATE
// =============================================================================

let isInitialized = false;
let config: ErrorTrackingConfig | null = null;
const isDev = import.meta.env.DEV;

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize error tracking service.
 * Should be called once at app startup.
 */
export function initErrorTracking(cfg: ErrorTrackingConfig): void {
  if (isInitialized) {
    console.warn("[ErrorTracking] Already initialized");
    return;
  }

  config = cfg;

  // Skip in development unless explicitly enabled
  if (isDev && !cfg.enableInDev) {
    console.info("[ErrorTracking] Disabled in development");
    isInitialized = true;
    return;
  }

  // Skip if no DSN
  if (!cfg.dsn) {
    console.warn("[ErrorTracking] No DSN provided, running in mock mode");
    isInitialized = true;
    return;
  }

  Sentry.init({
    dsn: cfg.dsn,
    environment: cfg.environment,
    release: cfg.release,
    sampleRate: cfg.sampleRate ?? 1.0,
    tracesSampleRate: cfg.tracesSampleRate ?? 0.1,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    beforeSend(event) {
      // Filter out known non-critical errors if needed
      return event;
    },
  });

  isInitialized = true;
  console.info("[ErrorTracking] Initialized with Sentry", {
    environment: cfg.environment,
    release: cfg.release,
  });
}

// =============================================================================
// ERROR TRACKING API
// =============================================================================

export const errorTracking = {
  /**
   * Capture an error with optional context.
   */
  captureError(error: Error | unknown, context?: ErrorContext): void {
    // Always log locally
    console.error("[Captured Error]", error, context);

    if (!isInitialized || !config?.dsn) return;

    try {
      if (context) {
        Sentry.withScope((scope) => {
          // Set user
          if (context.userId || context.accountId || context.telegramId) {
            scope.setUser({
              id: context.userId ?? context.accountId,
              ...(context.telegramId && { telegramId: context.telegramId }),
              ...(context.accountId && { accountId: context.accountId }),
            });
          }

          // Set tags
          if (context.tags) {
            Object.entries(context.tags).forEach(([key, value]) => {
              scope.setTag(key, value);
            });
          }

          // Set extras
          if (context.extra) {
            Object.entries(context.extra).forEach(([key, value]) => {
              scope.setExtra(key, value);
            });
          }

          // Set level
          if (context.level) {
            scope.setLevel(context.level as Sentry.SeverityLevel);
          }

          Sentry.captureException(error);
        });
      } else {
        Sentry.captureException(error);
      }
    } catch (e) {
      console.error("[ErrorTracking] Failed to capture error", e);
    }
  },

  /**
   * Capture a message (non-error event).
   */
  captureMessage(
    message: string,
    level: ErrorContext["level"] = "info",
    context?: ErrorContext
  ): void {
    console.info(`[Captured Message] ${message}`, context);

    if (!isInitialized || !config?.dsn) return;

    try {
      Sentry.captureMessage(message, level);
    } catch (e) {
      console.error("[ErrorTracking] Failed to capture message", e);
    }
  },

  /**
   * Add a breadcrumb for debugging.
   */
  addBreadcrumb(data: BreadcrumbData): void {
    if (!isInitialized || !config?.dsn) return;

    try {
      Sentry.addBreadcrumb({
        category: data.category ?? "app",
        message: data.message,
        data: data.data,
        level: data.level ?? "info",
        timestamp: Date.now() / 1000,
      });
    } catch (e) {
      console.error("[ErrorTracking] Failed to add breadcrumb", e);
    }
  },

  /**
   * Set user context for error reports.
   */
  setUser(user: UserContext | null): void {
    if (!isInitialized || !config?.dsn) return;

    try {
      if (user) {
        Sentry.setUser(user);
      } else {
        Sentry.setUser(null);
      }
    } catch (e) {
      console.error("[ErrorTracking] Failed to set user", e);
    }
  },

  /**
   * Set a global tag.
   */
  setTag(key: string, value: string): void {
    if (!isInitialized || !config?.dsn) return;

    try {
      Sentry.setTag(key, value);
    } catch (e) {
      console.error("[ErrorTracking] Failed to set tag", e);
    }
  },

  /**
   * Check if error tracking is enabled.
   */
  isEnabled(): boolean {
    return isInitialized && !!config?.dsn;
  },
};

// =============================================================================
// REACT ERROR BOUNDARY INTEGRATION
// =============================================================================

/**
 * Error handler for React Error Boundaries.
 */
export function handleReactError(
  error: Error,
  errorInfo: React.ErrorInfo
): void {
  errorTracking.captureError(error, {
    extra: {
      componentStack: errorInfo.componentStack,
    },
    tags: {
      errorType: "react-boundary",
    },
  });
}

// =============================================================================
// GLOBAL ERROR HANDLERS
// =============================================================================

/**
 * Setup global error handlers for uncaught errors.
 * Call once at app startup.
 */
export function setupGlobalErrorHandlers(): void {
  // Uncaught errors
  window.addEventListener("error", (event) => {
    errorTracking.captureError(event.error ?? new Error(event.message), {
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
      tags: {
        errorType: "uncaught",
      },
    });
  });

  // Unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    errorTracking.captureError(error, {
      tags: {
        errorType: "unhandled-rejection",
      },
    });
  });

  console.info("[ErrorTracking] Global error handlers installed");
}

// =============================================================================
// DEBUG (development only)
// =============================================================================

if (isDev) {
  window.__errorTracking = errorTracking;
}
